import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { MembersToolbar } from '../components/MembersToolbar';
import { InviteMemberDialog } from '../components/InviteMemberDialog';
import { InvitationsList } from '../components/InvitationsList';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useMembers } from '../hooks/useMembers';
import { useToggleMember } from '../hooks/useToggleMember';
import { useMembersFilters } from '../hooks/useMembersFilters';
import { getMemberColumns } from '../adapters/memberColumns';
import { useToast } from '@/shared/hooks/useToast';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import type { SortingState } from '@/shared/components/custom/list-view/types';

export function MembersPage() {
  const { t } = useTranslation('members');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [loadingMemberId, setLoadingMemberId] = useState<string | null>(null);

  const { search, debouncedSearch, setSearch, activeFiltersCount, resetFilters } =
    useMembersFilters();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>([]);

  const sortBy = sorting[0]?.id;
  const sortAscending = sorting[0] !== undefined ? !sorting[0].desc : undefined;

  const membersDataSource = useMembers(debouncedSearch, pageIndex, pageSize, sortBy, sortAscending);

  const pagination = usePagination({
    totalItems: membersDataSource.totalCount ?? 0,
    pageIndex,
    onPageChange: setPageIndex,
    pageSize,
    onPageSizeChange: setPageSize,
  });

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);
    setPageIndex(0);
  };

  const toggleMember = useToggleMember();
  const { toast } = useToast();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const members = membersDataSource.data;

  const handleStatusChange = async (memberId: string, activate: boolean) => {
    const member = members.find((m) => m.id === memberId);
    if (!member) return;

    const action = activate ? t('status.active').toLowerCase() : t('status.inactive').toLowerCase();

    const result = await confirm({
      title: t('confirm.changeStatus'),
      description: t('confirm.changeStatusDesc', { action, name: member.displayName }),
      confirmLabel: t('confirm.confirm'),
      cancelLabel: t('confirm.cancel'),
    });

    if (!result.confirmed) return;

    setLoadingMemberId(memberId);
    try {
      await toggleMember.mutateAsync({ memberId, activate });
      toast.success({
        title: activate ? t('toast.statusUpdatedActive') : t('toast.statusUpdatedPending'),
        description: activate
          ? t('toast.statusUpdatedActiveDesc', { name: member.displayName })
          : t('toast.statusUpdatedPendingDesc', { name: member.displayName }),
      });
    } catch {
      // global handler shows error toast
    }
    setLoadingMemberId(null);
  };

  const columns = getMemberColumns({
    onStatusChange: handleStatusChange,
    isLoading: (memberId: string) => loadingMemberId === memberId,
    t,
  });

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div>
        <MembersToolbar
          search={search}
          onSearchChange={setSearch}
          activeFiltersCount={activeFiltersCount}
          onResetFilters={resetFilters}
          onInviteClick={() => setIsInviteDialogOpen(true)}
        />

        <Tabs defaultValue="members" className="mt-4">
          <TabsList>
            <TabsTrigger value="members">{t('tabs.members')}</TabsTrigger>
            <TabsTrigger value="invitations">{t('tabs.invitations')}</TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="mt-4">
            <ListView
              data={membersDataSource.data}
              columns={columns}
              getRowId={(member) => member.id}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              manualSorting
              pagination={pagination}
              data-testid="members-list-table"
            />
          </TabsContent>

          <TabsContent value="invitations" className="mt-4">
            <InvitationsList />
          </TabsContent>
        </Tabs>

        <InviteMemberDialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen} />

        {isOpen && options && (
          <ConfirmDialog
            open={isOpen}
            onOpenChange={(open) => !open && handleCancel()}
            title={options.title}
            description={options.description}
            confirmLabel={options.confirmLabel}
            cancelLabel={options.cancelLabel}
            variant={options.variant}
            onConfirm={handleConfirm}
          />
        )}
      </div>
    </FeatureErrorBoundary>
  );
}
